import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import { Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import EditorJS from '@editorjs/editorjs';
import { useFormContext } from 'react-hook-form';
import uuid from 'uuid';

import { useFormikContext } from 'formik';
import { AudioInstruction, InstructionTypes } from '../../../../types/instructions';
import {
  setOpenAudioInstructionModal,
  getAudioListRequest,
} from '../../../../store/modules/script-editor/actions';
import { selectAudioInstruction } from '../../../../store/modules/script-editor/selectors';
import { selectProject } from '../../../../store/modules/projects/selectors';
import {
  InstructionEvent,
  InstructionActionTooltip,
} from '../types';
import { parseJsonToEditorStateWithInstruction } from '../../../../services/editorJS/utils';
import {
  emitInstructionEventByEditor, removeInstructionElement, setInstructionActionTooltip, sortDocumentsByTitle,
} from '../utils';
import {
  ModalComponent,
  InputComponent,
  SliderComponent, ProcessingMedia,
} from '../components';
import PreviewAudio from './PreviewAudio';
import useStyles from './styles';
import { ListParams } from '../../../../types/main';
import { getQueryFilter } from '../../../../utility/helpers';
import DuplicateBlock from '../../../../services/editorJS/DuplicateBlock';
import { notify } from '../../../../store/modules/notifications/actions';

import { FormSimpleAutoComplete } from '../../../index';
import { Option } from '../../../form-simple-autocomplete/FormSimpleAutoComplete';
import MediaFileMissingMessage from '../components/MediaFileMissingMessage';

type Props = {
  instanceEditor: EditorJS | undefined;
};

const scriptFieldName = 'script';

const defaultState = {
  comment: '',
  duration: 0.0,
  documentId: -1,
  level: 100,
};

const AudioInstructionModal = ({ instanceEditor }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { getValues, setValue } = useFormContext();
  const {
    isOpenModal, instructionId, isInline, audios,
  } = useSelector(selectAudioInstruction);
  const currentProject = useSelector(selectProject);
  const { setFieldValue } = useFormikContext();

  const [comment, setComment] = useState(defaultState.comment);
  const [duration, setDuration] = useState(defaultState.duration);
  const [level, setLevel] = useState(defaultState.level);
  const [documentId, setDocumentId] = useState<number>(defaultState.documentId);
  const [initialState, setInitialState] = useState(defaultState);

  useEffect(() => {
    if (isOpenModal) {
      setFieldValue('audio' as never, documentId);
    }
  }, [isOpenModal, documentId, setFieldValue]);

  useEffect(() => {
    if (!audios.loading) {
      (async () => {
        const { data } = currentProject;
        if (data) {
          const params: ListParams = {
            sort_dir: 'DESC',
            page_size: 50,
            filter: {
              project_id: data.id,
              is_media: 1,
              media_type_id: 3,
            },
          };
          if (audios.data === null) {
            dispatch(getAudioListRequest(params));
          } else {
            const projectId = getQueryFilter('project_id', audios.data.filters.active);
            if (projectId !== undefined && String(projectId.value) !== String(data.id)) {
              dispatch(getAudioListRequest(params));
            }
          }
        }
      })();
    }
  }, [dispatch, audios, currentProject]);

  useEffect(() => {
    if (instructionId) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instruction = scriptData.instructions.find(
          (item) => item.id === instructionId && item.type === InstructionTypes.audioInstruction,
        );

        if (instruction) {
          const { data } = instruction as AudioInstruction;
          setComment(instruction.comment);
          setDocumentId(data.documentId);
          setDuration(data.duration);
          setLevel(data.level);
          setInitialState({
            comment: instruction.comment,
            documentId: data.documentId,
            duration: data.duration,
            level: data.level,
          });
        } else {
          setDuration(defaultState.duration);
          setComment(defaultState.comment);
          setDocumentId(defaultState.documentId);
          setLevel(defaultState.level);
          setInitialState(defaultState);
        }
      })();
    } else {
      setDuration(defaultState.duration);
      setComment(defaultState.comment);
      setDocumentId(defaultState.documentId);
      setLevel(defaultState.level);
      setInitialState(defaultState);
    }
  }, [
    getValues,
    instructionId,
    setComment,
    setDocumentId,
    setDuration,
    setLevel,
    setInitialState,
  ]);

  const closeHandler = useCallback(() => {
    if (instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.SET_AUDIO_INSTRUCTION_SELECTION,
        {},
      );
    }
    dispatch(setOpenAudioInstructionModal(false));
  }, [dispatch, instanceEditor]);

  const getSummary = useCallback(() => {
    if (audios.data) {
      const article = audios.data.items.find((item) => item.document_id === documentId);
      if (article) {
        return article.ref.document?.title || 'Audio';
      }
    }
    if (comment.trim()) {
      return comment;
    }
    return 'Audio';
  }, [comment, audios, documentId]);

  const saveHandler = useCallback(() => {
    if (instanceEditor) {
      (async () => {
        const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
        const instructionIndex = scriptData.instructions.findIndex(
          (item) => item.id === instructionId && item.type === InstructionTypes.audioInstruction,
        );
        const audioInstructions: AudioInstruction = {
          id: instructionId,
          type: InstructionTypes.audioInstruction,
          comment,
          needsInfo: false,
          data: {
            duration,
            documentId,
            level,
          },
        };
        const summary = getSummary();
        if (instructionIndex !== -1) {
          scriptData.instructions[instructionIndex] = audioInstructions;
          if (isInline) {
            setInstructionActionTooltip(
              {
                instructionId,
                tooltip: `${InstructionActionTooltip.audio}: ${summary}`,
                scriptBlocks: scriptData.blocks,
              },
            );
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.UPDATE_AUDIO_ACTION_EVENT_LISTENERS,
              {
                instanceEditor,
                refEditor: window.scriptEditorRef!,
              },
            );
          }
          instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
        } else {
          const id = (instructionId.trim()) ? instructionId : uuid();
          audioInstructions.id = id;
          scriptData.instructions.push(audioInstructions);
          if (isInline) {
            emitInstructionEventByEditor(
              instanceEditor,
              InstructionEvent.SAVE_AUDIO_INSTRUCTION,
              { instructionId: id, tooltip: summary },
            );
          }
        }
        if (!isInline) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.SAVE_AUDIO_INSTRUCTION_BLOCK,
            { instructionId: audioInstructions.id, text: summary, tooltip: summary },
          );
        }
        setValue(scriptFieldName, JSON.stringify(scriptData));
        closeHandler();
      })();
    }
  }, [
    instanceEditor,
    getValues,
    setValue,
    documentId,
    duration,
    comment,
    level,
    instructionId,
    closeHandler,
    isInline,
    getSummary,
  ]);

  const cancelHandler = useCallback(() => {
    (async () => {
      setComment(initialState.comment);
      setDocumentId(initialState.documentId);
      setLevel(initialState.level);
      setDuration(initialState.duration);
      const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
      const isInstructionCreated = scriptData.instructions.some(
        (item) => item.id === instructionId && item.type === InstructionTypes.audioInstruction,
      );
      if (instanceEditor) {
        if (!isInstructionCreated) {
          emitInstructionEventByEditor(
            instanceEditor,
            InstructionEvent.REMOVE_INSTRUCTION_BLOCK_WITHOUT_SAVING,
            {},
          );
        }
        emitInstructionEventByEditor(
          instanceEditor,
          InstructionEvent.CANCEL_INSTRUCTION,
          {},
        );
      }
      closeHandler();
    })();
  }, [
    setComment,
    setDuration,
    setLevel,
    initialState,
    closeHandler,
    instanceEditor,
    instructionId,
    getValues,
  ]);

  const removeHandler = useCallback(() => {
    if (!isInline && instanceEditor) {
      emitInstructionEventByEditor(
        instanceEditor,
        InstructionEvent.REMOVE_INSTRUCTION_BLOCK,
        {},
      );
    } else {
      removeInstructionElement({ instructionId, isInline })
        .then(() => {
          if (instanceEditor) {
            instanceEditor.events.emit(InstructionEvent.SUBMIT_DATA, {});
          }
        });
    }
    closeHandler();
  }, [closeHandler, instructionId, isInline, instanceEditor]);

  const audioList = useMemo(() => {
    if (audios.data) {
      const sortedAudios = sortDocumentsByTitle(audios.data.items).map((item) => ({
        id: item.document_id,
        label: item.ref.document!.title,
      }));
      return [{ id: -1, label: 'None' }, ...sortedAudios];
    }
    return [];
  }, [audios.data]);

  const media = useMemo(() => {
    if (audios.data) {
      return audios.data.items.find((item) => item.document_id === documentId);
    }

    return undefined;
  }, [documentId, audios.data]);

  const duplicate = useCallback(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues().script);
    const notifHandler = () => dispatch(
      notify({
        type: 'success',
        icon: 'copy',
        message: 'Instruction duplicated.',
      }),
    );
    if (instanceEditor) {
      const duplicateBlockService = new DuplicateBlock(instanceEditor, notifHandler, setValue, instructionId, scriptData);
      if (!isInline) {
        duplicateBlockService.duplicateBlock();
      } else {
        duplicateBlockService.duplicateInline();
      }
    }
  }, [instanceEditor, getValues, instructionId, isInline, setValue, dispatch]);


  const showKebabMenu = useMemo(() => {
    const scriptData = parseJsonToEditorStateWithInstruction(getValues()[scriptFieldName]);
    return scriptData.instructions.some(
      (item) => item.id === instructionId,
    ) || scriptData.blocks.some((item) => item.data.instructionId === instructionId);
  }, [getValues, instructionId]);

  const onChangeAudioFileHandler = useCallback((option?: Option) => {
    if (option) {
      setDocumentId(parseInt(option.id.toString(), 10));
    }
  }, []);

  return (
    <ModalComponent
      open={isOpenModal}
      title={getSummary()}
      saveHandler={saveHandler}
      cancelHandler={cancelHandler}
      deleteHandler={removeHandler}
      duplicate={duplicate}
      showKebabMenu={showKebabMenu}
    >
      <Grid container spacing={3} justify="space-between">
        <Grid item xs={12}>
          <InputComponent
            type="textarea"
            label="Comment"
            value={comment}
            onChange={setComment}
            autoFocus
          />
        </Grid>
        <Grid item container spacing={3} xs={6} alignContent="flex-start">
          <Grid item xs={12}>
            <FormSimpleAutoComplete
              label="Audio"
              options={audioList}
              name="audio"
              callback={onChangeAudioFileHandler}
              isClearValueIfOptionNull
            />
          </Grid>
          <Grid item xs={12}>
            <SliderComponent title="Level" value={level} onChange={setLevel} />
          </Grid>
          {/* - 2020.05,bn: disabled until we have clip start/stop selection
          <Grid item xs={12}>
            <InputComponent
              label="Duration"
              value={duration}
              onChange={setDuration}
            />
          </Grid>
          */}
        </Grid>
        <Grid item container spacing={3} xs={6} alignItems="flex-start" alignContent="flex-start">
          {/* <Grid item xs={12}> */}
          {/*  <p className={classes.previewTitle}>Preview</p> */}
          {/*  {media && <PreviewAudio media={media} />} */}
          {/* </Grid> */}
          <p className={classes.previewTitle}>Preview</p>
          <Grid item container xs={12} alignItems="center" justify="center" className={classes.previewContainer}>
            {
              documentId < 0
                ? <MediaFileMissingMessage />
                : (
                  documentId !== -1 ? <ProcessingMedia />
                    : media && <PreviewAudio media={media} />
                )
            }
          </Grid>
        </Grid>
      </Grid>
    </ModalComponent>
  );
};

export default AudioInstructionModal;
